import {useState, useRef, useEffect} from "react";
import {useSelector} from "react-redux";
import * as XLSX from "xlsx";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";

import {PlayerAPI} from "@/api/player";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";

import {ERROR_OPERATION} from "@/utils/constants";
import {COLORS} from "@/style/utils";

import "./index.scss";

// ── Storage keys ──────────────────────────────────────────────────────────────

const STORAGE_KEYS = {
	teamStats: "wyscout_team_stats",
	playerStats: "wyscout_player_stats",
};

// ── Parse helpers ─────────────────────────────────────────────────────────────

function parseTeamStatsXLSX(workbook) {
	const sheet = workbook.Sheets[workbook.SheetNames[0]];
	const raw = XLSX.utils.sheet_to_json(sheet, {header: 1, defval: ""});

	if (raw.length < 3) return null;

	const headers = raw[0];
	const teamName = raw[1]?.[0] || "Squadra";
	const dataRows = raw.slice(2).filter((row) => row[0]);

	const matches = dataRows.map((row) => {
		const match = {};
		headers.forEach((h, i) => {
			if (h) match[h] = row[i];
		});
		return match;
	});

	// Calcola medie stagionali
	const numericAvg = (key) => {
		const vals = matches.map((m) => parseFloat(m[key])).filter((v) => !isNaN(v));
		return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
	};

	const numericSum = (key) => {
		const vals = matches.map((m) => parseFloat(m[key])).filter((v) => !isNaN(v));
		return vals.reduce((s, v) => s + v, 0);
	};

	return {
		teamName,
		matchCount: matches.length,
		importedAt: new Date().toISOString(),
		matches,
		seasonAverages: {
			goals: numericAvg("Goal"),
			xG: numericAvg("xG"),
			possession: numericAvg("Possesso palla, %"),
			ppda: numericAvg("PPDA"),
		},
		seasonTotals: {
			goals: numericSum("Goal"),
			goalsAgainst: numericSum("Goal subiti"),
		},
	};
}

function parsePlayerStatsXLSX(workbook) {
	const sheet = workbook.Sheets[workbook.SheetNames[0]];
	const raw = XLSX.utils.sheet_to_json(sheet, {header: 1, defval: ""});

	if (raw.length < 2) return null;

	const headers = raw[0];
	const dataRows = raw.slice(1).filter((row) => row[0]);

	const matches = dataRows.map((row) => {
		const match = {};
		headers.forEach((h, i) => {
			if (h) match[h] = row[i];
		});
		return match;
	});

	const numericSum = (key) => {
		const vals = matches.map((m) => parseFloat(m[key])).filter((v) => !isNaN(v));
		return vals.reduce((s, v) => s + v, 0);
	};

	const totalMinutes = numericSum("Minuti giocati");

	return {
		matchCount: matches.length,
		importedAt: new Date().toISOString(),
		matches,
		totals: {
			minutes: totalMinutes,
			goals: numericSum("Goal"),
			assists: numericSum("Assist"),
			xG: numericSum("xG"),
			yellowCards: numericSum("Cartellini gialli"),
			redCards: numericSum("Cartellini rossi"),
			duelsWon: numericSum("Duelli / vinti"),
			interceptions: numericSum("Intercetti palla"),
		},
		per90: totalMinutes > 0
			? {
					goals: (numericSum("Goal") / totalMinutes) * 90,
					assists: (numericSum("Assist") / totalMinutes) * 90,
					xG: (numericSum("xG") / totalMinutes) * 90,
					duelsWon: (numericSum("Duelli / vinti") / totalMinutes) * 90,
					interceptions: (numericSum("Intercetti palla") / totalMinutes) * 90,
				}
			: null,
	};
}

// ── Component ─────────────────────────────────────────────────────────────────

function ImportDataPage(props) {
	const {handleNotification} = props;

	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [teamData, setTeamData] = useState(() => {
		const saved = localStorage.getItem(STORAGE_KEYS.teamStats);
		return saved ? JSON.parse(saved) : null;
	});
	const [playerImports, setPlayerImports] = useState(() => {
		const saved = localStorage.getItem(STORAGE_KEYS.playerStats);
		return saved ? JSON.parse(saved) : [];
	});

	const [allPlayers, setAllPlayers] = useState([]);
	const [pendingFile, setPendingFile] = useState(null);
	const [selectedPlayer, setSelectedPlayer] = useState(null);

	const teamInputRef = useRef(null);
	const playerInputRef = useRef(null);

	useEffect(() => {
		const loadPlayers = async () => {
			try {
				const [internal, scouting] = await Promise.all([
					PlayerAPI.list({prospect: false, seasonId}),
					PlayerAPI.list({prospect: true, seasonId}),
				]);
				const format = (list, tag) =>
					(list || []).map((p) => ({
						value: p.id,
						label: `${p.lastName} ${p.firstName}`,
						tag,
					}));
				setAllPlayers([
					...format(internal, "Interno"),
					...format(scouting, "Scouting"),
				]);
			} catch {
				handleNotification(ERROR_OPERATION, "error");
			}
		};
		loadPlayers();
	}, [seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Team import ───────────────────────────────────────────────────────────

	const handleTeamFile = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setLoading(true);
		try {
			const buffer = await file.arrayBuffer();
			const workbook = XLSX.read(buffer, {type: "array"});
			const parsed = parseTeamStatsXLSX(workbook);

			if (!parsed || !parsed.matches.length) {
				handleNotification("File non valido o vuoto", "error");
				return;
			}

			parsed.fileName = file.name;
			setTeamData(parsed);
			localStorage.setItem(STORAGE_KEYS.teamStats, JSON.stringify(parsed));
			handleNotification(`Importate ${parsed.matchCount} partite per ${parsed.teamName}`, "success");
		} catch {
			handleNotification("Errore nella lettura del file", "error");
		} finally {
			setLoading(false);
			e.target.value = "";
		}
	};

	// ── Player import ─────────────────────────────────────────────────────────

	const handlePlayerFile = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setLoading(true);
		try {
			const buffer = await file.arrayBuffer();
			const workbook = XLSX.read(buffer, {type: "array"});
			const parsed = parsePlayerStatsXLSX(workbook);

			if (!parsed || !parsed.matches.length) {
				handleNotification("File non valido o vuoto", "error");
				return;
			}

			parsed.fileName = file.name;
			setPendingFile(parsed);
			setSelectedPlayer(null);
		} catch {
			handleNotification("Errore nella lettura del file", "error");
		} finally {
			setLoading(false);
			e.target.value = "";
		}
	};

	const confirmPlayerImport = () => {
		if (!pendingFile || !selectedPlayer) return;

		const entry = {
			...pendingFile,
			playerId: selectedPlayer.value,
			playerName: selectedPlayer.label,
		};

		const updated = [...playerImports.filter((p) => p.playerId !== entry.playerId), entry];
		setPlayerImports(updated);
		localStorage.setItem(STORAGE_KEYS.playerStats, JSON.stringify(updated));
		handleNotification(`Importate ${entry.matchCount} partite per ${entry.playerName}`, "success");
		setPendingFile(null);
		setSelectedPlayer(null);
	};

	const cancelPlayerImport = () => {
		setPendingFile(null);
		setSelectedPlayer(null);
	};

	const removePlayerImport = (playerId) => {
		const updated = playerImports.filter((p) => p.playerId !== playerId);
		setPlayerImports(updated);
		localStorage.setItem(STORAGE_KEYS.playerStats, JSON.stringify(updated));
	};

	const removeTeamImport = () => {
		setTeamData(null);
		localStorage.removeItem(STORAGE_KEYS.teamStats);
	};

	const fmt = (v, d = 2) => (v != null && !isNaN(v) ? Number(v).toFixed(d) : "—");

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs elements={[]} current="Import Dati" />

			<div className="import-page">
				{/* ── Team stats ─────────────────────────────────── */}
				<VLFCard
					title="Statistiche Squadra (Wyscout)"
					openable={false}
					content={
						<div className="import-section">
							<p className="import-section__desc">
								Importa il file XLSX esportato da Wyscout con le statistiche di squadra partita per partita.
							</p>

							<input
								type="file"
								accept=".xlsx,.xls"
								ref={teamInputRef}
								onChange={handleTeamFile}
								style={{display: "none"}}
							/>

							{teamData ? (
								<div className="import-section__result">
									<div className="import-section__result-header">
										<CheckCircleIcon sx={{color: COLORS.good, fontSize: 20}} />
										<div>
											<div className="import-section__result-title">
												{teamData.teamName} — {teamData.matchCount} partite importate
											</div>
											<div className="import-section__result-sub">
												{teamData.fileName} — importato il {new Date(teamData.importedAt).toLocaleDateString("it-IT")}
											</div>
										</div>
										<button className="import-section__remove-btn" onClick={removeTeamImport}>
											<DeleteIcon sx={{fontSize: 16}} />
										</button>
									</div>
									<div className="import-section__kpis">
										<div className="import-section__kpi">
											<span className="import-section__kpi-value">{fmt(teamData.seasonAverages?.goals, 1)}</span>
											<span className="import-section__kpi-label">Gol/partita</span>
										</div>
										<div className="import-section__kpi">
											<span className="import-section__kpi-value">{fmt(teamData.seasonAverages?.xG, 2)}</span>
											<span className="import-section__kpi-label">xG medio</span>
										</div>
										<div className="import-section__kpi">
											<span className="import-section__kpi-value">{fmt(teamData.seasonAverages?.possession, 1)}%</span>
											<span className="import-section__kpi-label">Possesso</span>
										</div>
										<div className="import-section__kpi">
											<span className="import-section__kpi-value">{fmt(teamData.seasonAverages?.ppda, 1)}</span>
											<span className="import-section__kpi-label">PPDA</span>
										</div>
									</div>
									<button
										className="import-section__upload-btn"
										onClick={() => teamInputRef.current?.click()}
									>
										<UploadFileIcon sx={{fontSize: 16}} />
										Aggiorna file
									</button>
								</div>
							) : (
								<button
									className="import-section__upload-btn import-section__upload-btn--large"
									onClick={() => teamInputRef.current?.click()}
								>
									<UploadFileIcon sx={{fontSize: 20}} />
									Carica file XLSX squadra
								</button>
							)}
						</div>
					}
				/>

				{/* ── Player stats ───────────────────────────────── */}
				<VLFCard
					title="Statistiche Giocatori (Wyscout)"
					openable={false}
					content={
						<div className="import-section">
							<p className="import-section__desc">
								Importa i file XLSX esportati da Wyscout per i singoli giocatori e collegali a un giocatore a database.
							</p>

							<input
								type="file"
								accept=".xlsx,.xls"
								ref={playerInputRef}
								onChange={handlePlayerFile}
								style={{display: "none"}}
							/>

							{pendingFile ? (
								<div className="import-section__pending">
									<div className="import-section__pending-header">
										<UploadFileIcon sx={{fontSize: 16, color: COLORS.primary}} />
										<span className="import-section__pending-file">
											{pendingFile.fileName} — {pendingFile.matchCount} partite
										</span>
									</div>
									<div className="import-section__pending-select">
										<VLFSelectNew
											options={allPlayers}
											value={selectedPlayer}
											onChange={setSelectedPlayer}
											label="Collega a giocatore"
											placeholder="Cerca giocatore..."
											isClearable
										/>
									</div>
									<div className="import-section__pending-actions">
										<button
											className="import-section__upload-btn"
											onClick={confirmPlayerImport}
											disabled={!selectedPlayer}
										>
											<LinkIcon sx={{fontSize: 16}} />
											Collega e importa
										</button>
										<button
											className="import-section__cancel-btn"
											onClick={cancelPlayerImport}
										>
											Annulla
										</button>
									</div>
								</div>
							) : (
								<button
									className="import-section__upload-btn import-section__upload-btn--large"
									onClick={() => playerInputRef.current?.click()}
								>
									<UploadFileIcon sx={{fontSize: 20}} />
									Carica file XLSX giocatore
								</button>
							)}

							{playerImports.length > 0 && (
								<div className="import-section__players">
									{playerImports.map((p) => (
										<div key={p.playerId} className="import-section__player-row">
											<CheckCircleIcon sx={{color: COLORS.good, fontSize: 16}} />
											<div className="import-section__player-info">
												<span className="import-section__player-name">{p.playerName}</span>
												<span className="import-section__player-meta">
													{p.matchCount} partite · {p.totals?.minutes || 0} min · {p.totals?.goals || 0} gol · {p.totals?.assists || 0} assist
												</span>
											</div>
											{p.per90 && (
												<div className="import-section__player-per90">
													<span>{fmt(p.per90.goals, 2)} gol/90</span>
													<span>{fmt(p.per90.xG, 2)} xG/90</span>
												</div>
											)}
											<button
												className="import-section__remove-btn"
												onClick={() => removePlayerImport(p.playerId)}
											>
												<DeleteIcon sx={{fontSize: 14}} />
											</button>
										</div>
									))}
								</div>
							)}
						</div>
					}
				/>
			</div>
		</Page>
	);
}

export default ImportDataPage;
